import { ApiTwoTone, DeleteTwoTone, ExportOutlined } from "@ant-design/icons";
import { Button, Flex, Space } from "antd";
import Card from "antd/lib/card";
import { GraphTooltip } from "components/GraphTooltip";
import { useHasRole } from "components/Login/hook";
import { deleteDoc } from "firebase/firestore";
import capitalize from "lodash/capitalize";
import React, { ReactElement, useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import { FirestoreDoc } from "../../providers/FirestoreProvider";
import { LogoBreadcrumb } from "../Breadcrumb";
import { Logo } from "./Logo";
import { OAuth2, OAuth2Props } from "./OAuth2";
import { DeleteModal } from "./components/DeleteModal";
import { allGaIntegrations } from "./constants";

export type IntegrationCardOverrides = {
  title?: string;
  canRemove?: boolean;
  showBreadcrumbs?: boolean;
  refPath?: string;
  docsUrl?: string;
  /** If true, docs link is only shown at component level, not parent level */
  hideParentDocsLink?: boolean;
};

// necessary to override the default border since
// when bordered={false} is passed in antd
// renders a box shadow instead
const UnborderedCard = styled(Card)`
  border: none;
`;

type IntegrationCardBaseProps = {
  integration: string;
  component?: string;
  componentTitle?: string;
  logo: ReactElement | string;
  integrationDoc?: FirestoreDoc<any>;
  onRemove?: () => Promise<void>;
};

type IntegrationCardProps = IntegrationCardBaseProps & IntegrationCardOverrides;

export const OAuth2IntegrationCard: React.FC<
  React.PropsWithChildren<
    IntegrationCardProps &
      OAuth2Props & {
        preamble?: ReactElement;
      }
  >
> = ({
  canRemove,
  children,
  integration,
  integrationDoc,
  logo,
  onRemove,
  preamble,
  title,
  showBreadcrumbs = true,
  docsUrl,
  ...oauth2Props
}) => {
  return (
    <IntegrationCard
      canRemove={canRemove}
      onRemove={onRemove}
      integrationDoc={integrationDoc}
      {...{ integration, logo, title, showBreadcrumbs, docsUrl }}
    >
      <Space direction="vertical">
        {preamble}
        <OAuth2 integration={integration} {...oauth2Props}>
          {children}
        </OAuth2>
      </Space>
    </IntegrationCard>
  );
};

export const IntegrationCard: React.FC<
  React.PropsWithChildren<IntegrationCardProps>
> = ({
  children,
  logo,
  integration,
  integrationDoc,
  component,
  componentTitle,
  title,
  canRemove,
  onRemove,
  showBreadcrumbs = true,
  refPath = "/integrations",
  docsUrl,
  hideParentDocsLink,
}) => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const isOwner = useHasRole("owner");

  const basePath = useMemo(() => `/o/${orgSlug}${refPath}`, [orgSlug, refPath]);

  const toggleDeleteModal = useCallback(
    () => setIsDeleteModalOpen(!isDeleteModalOpen),
    [isDeleteModalOpen]
  );

  const removeIntegration = useCallback(async () => {
    setIsDeleting(true);
    setIsDeleteModalOpen(false);
    if (onRemove) await onRemove();
    else if (integrationDoc?.ref) {
      await deleteDoc(integrationDoc.ref).catch(console.error);
    }
    setIsDeleting(false);
  }, [integrationDoc, onRemove]);

  const coalescedCanRemove = useMemo(
    () =>
      isOwner &&
      (canRemove !== undefined ? canRemove : !!(onRemove ?? integrationDoc)),
    [isOwner, canRemove, onRemove, integrationDoc]
  );

  const cardTitle = useMemo(
    () => title ?? capitalize(integration),
    [integration, title]
  );
  const breadcrumbItems = useMemo(
    () => [
      {
        icon: <ApiTwoTone style={{ fontSize: 20 }} />,
        to: basePath,
        label: "Integrations",
      },
      {
        icon: <Logo logo={logo} title={cardTitle} />,
        to: component && `${basePath}/${integration}`,
        label: cardTitle,
      },
      ...(component
        ? [{ label: componentTitle ?? capitalize(component) }]
        : []),
    ],
    [basePath, cardTitle, component, componentTitle, integration, logo]
  );

  // Only show docs button at the parent level (no component selected)
  // Component-level buttons are rendered by the Component component
  const resolvedDocsUrl = useMemo(() => {
    if (component) return undefined; // Let Component handle it
    if (hideParentDocsLink) return undefined; // Docs shown at component level only
    if (docsUrl) return docsUrl;
    return allGaIntegrations[integration]?.docs.install;
  }, [docsUrl, integration, component, hideParentDocsLink]);

  return (
    <>
      <UnborderedCard
        style={{ maxWidth: 800 }}
        size="small"
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Flex gap={24} align="top">
              {showBreadcrumbs ? (
                <LogoBreadcrumb items={breadcrumbItems} />
              ) : (
                <div style={{ display: "inline-flex", alignItems: "center" }}>
                  <Logo logo={logo} title={cardTitle} />
                  <span style={{ marginLeft: "0.5em" }}>{cardTitle}</span>
                </div>
              )}
              {resolvedDocsUrl && (
                <Button
                  type="link"
                  size="small"
                  className="button-link"
                  href={resolvedDocsUrl}
                  target="_blank"
                  rel="noreferrer"
                  icon={<ExportOutlined />}
                >
                  View Documentation
                </Button>
              )}
            </Flex>
            {coalescedCanRemove ? (
              <GraphTooltip
                title={`Uninstall ${cardTitle} integration`}
                placement="topLeft"
              >
                <Button
                  loading={isDeleting}
                  icon={<DeleteTwoTone />}
                  style={{ border: "none" }}
                  onClick={toggleDeleteModal}
                />
              </GraphTooltip>
            ) : null}
          </div>
        }
      >
        {children}
      </UnborderedCard>
      <DeleteModal
        id={cardTitle}
        isOpen={isDeleteModalOpen}
        toggleOpen={toggleDeleteModal}
        onRemove={removeIntegration}
      />
    </>
  );
};
